import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Grid,
  Card,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
  CardContent,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Formik, Form, FieldArray } from 'formik';

import {
  dummySlaRiskTimes,
  dummyCriticalWords,
  dummyAutoAlerts,
  ALERT_ACTIONS,
  getAlertActionLabel,
} from './mappers/SlaMapper';
import { styled } from '@mui/material/styles';
import { BreadCrumbsAndTitle } from '../../../components/generics/BreadCrumbsAndTitle';
import { alertInformativo } from '../../../commons/texts';
import { AlertsInformativos } from '../../../components/generics/AlertsInformativos';
import { useResponseHandler } from '../../../hooks/useResponseHandler';
import {
  getSlaStore,
  storeCreateSla,
  storeUpdateSla,
  storeCreateAutoAlert,
  storeUpdateAutoAlert,
  storeDeleteAutoAlert,
  storeCreateKeyword,
  storeDeleteKeyword,
} from '../../../store/sla';
import validationSchema from './formik/formModels/validationSchema';
import InputField from '../../../components/formFields/InputField';
import SelectField from '../../../components/formFields/selectField';
import { ModalConfirmDeactivation } from '../../../components/modals/ModalConfirmDeactivation';

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: 'bold',
  textAlign: 'center',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  textAlign: 'center',
}));

export const Sla = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { sla_list, alert_risk_levels, alert_risk_levels_actions_select, critical_words } =
    useSelector((state: any) => state.sla);

  const extractList = (list: any): any[] => {
    if (!list) return [];
    if (Array.isArray(list)) return list;
    if (list.data && Array.isArray(list.data)) return list.data;
    if (list.items && Array.isArray(list.items)) return list.items;
    return [];
  };
  const dataList = extractList(sla_list);
  const actionsList = extractList(alert_risk_levels_actions_select);

  useEffect(() => {
    const list = extractList(alert_risk_levels);
    setAutoAlerts((prev) => {
      const newItems = prev.filter((p) => p.isNew);

      const mapped = list.map((alert: any) => {
        const sla = dataList.find((s: any) => String(s.id) === String(alert.riskLevelId));
        return {
          id: alert.value || alert.id,
          sourceSlaId: alert.riskLevelId,
          timeWithoutManagement: sla ? `${sla.resolutionValue} ${sla.resolutionUnit}` : 'N/A',
          alertLevel: sla ? sla.level : 'Desconocido',
          action: alert.actionType,
          color: sla ? sla.color : '#ccc',
          isNew: false,
          isEditing: false,
        };
      });
      return [...mapped, ...newItems];
    });
  }, [alert_risk_levels, dataList]);

  // Usar sla_list del store si existe, sino el dummy por defecto
  const initialSlaTimes = dataList.length > 0 ? dataList : dummySlaRiskTimes;

  const [autoAlerts, setAutoAlerts] = useState<any[]>([]);

  // RESPONSE HANDLER HOOK
  const { handleResponse, ResponseComponents } = useResponseHandler();

  const [newWord, setNewWord] = useState('');
  const [editModeSla, setEditModeSla] = useState(false);

  const [openDeactivateModal, setOpenDeactivateModal] = useState(false);
  const [alertToDeleteId, setAlertToDeleteId] = useState<string | null>(null);

  const [openSlaDeactivateModal, setOpenSlaDeactivateModal] = useState(false);
  const [slaToDeleteInfo, setSlaToDeleteInfo] = useState<{
    index: number;
    removeFn: (index: number) => void;
  } | null>(null);

  const [openKeywordDeactivateModal, setOpenKeywordDeactivateModal] = useState(false);
  const [keywordToDeleteId, setKeywordToDeleteId] = useState<string | null>(null);

  const confirmDeleteSla = () => {
    if (slaToDeleteInfo) {
      slaToDeleteInfo.removeFn(slaToDeleteInfo.index);
      setSlaToDeleteInfo(null);

      // Automatically submit the form to update the backend
      setTimeout(() => {
        const btn = document.getElementById('btnSubmitSla');
        if (btn) btn.click();
      }, 100);
    }
  };

  useEffect(() => {
    getDataSla();
  }, []);

  const getDataSla = async () => {
    const response = await dispatch(getSlaStore());
    await handleResponse(response, 'Obteniendo datos de SLA', false);
  };

  const handleSubmit = async (values: any, actions: any) => {
    const isCreate = dataList.length === 0;

    const payload = values.slaTimes.map((item: any) => {
      const { id, ...rest } = item;
      return isCreate || (id && String(id).startsWith('sla-')) ? rest : item;
    });

    let response;
    if (isCreate) {
      response = await dispatch(storeCreateSla(payload));
    } else {
      response = await dispatch(storeUpdateSla(payload));
    }

    await handleResponse(
      response,
      isCreate ? 'Guardando configuración inicial de SLA' : 'Actualizando configuración de SLA'
    );
    if (response.ok) {
      setEditModeSla(false);
    }
    actions.setSubmitting(false);
  };

  // DND handlers
  const handleDragStart = (e: React.DragEvent, item: any) => {
    e.dataTransfer.setData('text/plain', JSON.stringify(item));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault(); // allow drop
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));

      // Verifica si ya fue agregada
      const isAlreadyAdded = autoAlerts.some((alert: any) => alert.sourceSlaId === data.id);
      if (isAlreadyAdded) return;

      // Create a new alert based on the SLA item
      const newAlert = {
        id: `a-${Date.now()}`,
        sourceSlaId: data.id,
        timeWithoutManagement: `${data.resolutionValue} ${data.resolutionUnit}`,
        alertLevel: data.level,
        action: actionsList.length > 0 ? actionsList[0].value : 'NOTIFICACION_AL_RESPONSABLE', // default action
        color: data.color || '#ccc',
        isNew: true,
        isEditing: false,
      };
      setAutoAlerts([...autoAlerts, newAlert]);
    } catch (error) {
      console.error('Error on drop', error);
    }
  };

  // Words Handlers
  const handleAddWord = async () => {
    if (newWord.trim() !== '') {
      const response = await dispatch(storeCreateKeyword({ keyword: newWord.trim() }));
      await handleResponse(response, 'Agregando palabra clave');
      if (response.ok) {
        setNewWord('');
      }
    }
  };

  const handleDeleteKeywordClick = (id: string) => {
    setKeywordToDeleteId(id);
    setOpenKeywordDeactivateModal(true);
  };

  const confirmDeleteKeyword = async () => {
    if (!keywordToDeleteId) return;
    const response = await dispatch(storeDeleteKeyword(keywordToDeleteId));
    await handleResponse(response, 'Eliminando palabra clave');
    setKeywordToDeleteId(null);
  };

  // Auto Alerts Handlers
  const handleAlertActionChange = (id: string, newAction: string) => {
    setAutoAlerts(
      autoAlerts.map((item) => (item.id === id ? { ...item, action: newAction } : item))
    );
  };

  const handleSaveAlert = async (id: string) => {
    const alertToSave = autoAlerts.find((item) => item.id === id);
    if (!alertToSave) return;

    const isNew = alertToSave.isNew;

    const payload = {
      actionType: alertToSave.action,
      riskLevelId: alertToSave.sourceSlaId,
    };

    let response;
    if (isNew) {
      response = await dispatch(storeCreateAutoAlert(payload));
    } else {
      response = await dispatch(storeUpdateAutoAlert(id, payload));
    }

    await handleResponse(
      response,
      isNew ? 'Guardando alerta automática' : 'Actualizando alerta automática'
    );

    setAutoAlerts(
      autoAlerts.map((item) =>
        item.id === id ? { ...item, isNew: false, isEditing: false } : item
      )
    );
  };

  const handleEditAlert = (id: string) => {
    setAutoAlerts(autoAlerts.map((item) => (item.id === id ? { ...item, isEditing: true } : item)));
  };

  const handleDeleteAlert = (id: string) => {
    const alertToDelete = autoAlerts.find((item) => item.id === id);
    if (!alertToDelete) return;

    if (alertToDelete.isNew) {
      setAutoAlerts(autoAlerts.filter((item) => item.id !== id));
      return;
    }

    setAlertToDeleteId(id);
    setOpenDeactivateModal(true);
  };

  const confirmDeleteAlert = async () => {
    if (!alertToDeleteId) return;

    const response = await dispatch(storeDeleteAutoAlert(alertToDeleteId));
    await handleResponse(response, 'Eliminando alerta automática');

    if (response.ok) {
      setAutoAlerts(autoAlerts.filter((item) => item.id !== alertToDeleteId));
    }

    setAlertToDeleteId(null);
  };
  console.log('initialSlaTimes ', initialSlaTimes);
  return (
    <Box sx={{ width: '100%', p: 1 }}>
      {ResponseComponents}
      <ModalConfirmDeactivation
        open={openDeactivateModal}
        setOpen={setOpenDeactivateModal}
        onConfirm={confirmDeleteAlert}
        title="Eliminar Alerta Automática"
        description="¿Está seguro de eliminar esta alerta automática?"
      />
      <ModalConfirmDeactivation
        open={openKeywordDeactivateModal}
        setOpen={setOpenKeywordDeactivateModal}
        onConfirm={confirmDeleteKeyword}
        title="Eliminar Palabra Clave"
        description="¿Está seguro de eliminar esta palabra clave?"
      />
      <ModalConfirmDeactivation
        open={openSlaDeactivateModal}
        setOpen={setOpenSlaDeactivateModal}
        onConfirm={confirmDeleteSla}
        title="Eliminar Tiempo de SLA"
        description="¿Está seguro de eliminar este tiempo de SLA de la lista? Recuerde guardar los cambios para aplicarlos."
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <BreadCrumbsAndTitle
            title="SLA y Alertas"
            mainModule="Calificaciones"
            action=""
            parent="SLA y Alertas"
            icon="AccessTime"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <AlertsInformativos type={'info'} title={'info'} content={alertInformativo.sla} />
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 3, mt: 1 }} />

      <Typography variant="h6" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
        4. CONFIGURACIÓN DE SLA Y ALERTAS (PARÁMETROS DEL SISTEMA)
      </Typography>

      <Formik
        initialValues={{ slaTimes: initialSlaTimes }}
        validationSchema={validationSchema[0]}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, isSubmitting, resetForm }) => (
          <Form>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* A. TIEMPOS DE SLA POR NIVEL DE RIESGO */}
              <Grid size={{ xs: 12, md: 12 }}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 2,
                    boxShadow: 2,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                      A. TIEMPOS DE SLA POR NIVEL DE RIESGO (EJEMPLO)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {dataList.length === 0 && !editModeSla && values.slaTimes?.length > 0 && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={() => document.getElementById('btnSubmitSla')?.click()}
                            disabled={isSubmitting}
                          >
                            Guardar
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={() => setEditModeSla(true)}
                            disabled={isSubmitting}
                          >
                            Editar
                          </Button>
                        </>
                      )}

                      {(dataList.length > 0 || editModeSla) && values.slaTimes?.length > 0 && (
                        <>
                          <IconButton
                            onClick={() => {
                              if (editModeSla) {
                                document.getElementById('btnSubmitSla')?.click();
                              } else {
                                setEditModeSla(true);
                              }
                            }}
                            color="primary"
                            size="small"
                            disabled={isSubmitting}
                          >
                            {editModeSla ? <SaveIcon /> : <EditIcon />}
                          </IconButton>
                          {editModeSla && (
                            <Button
                              size="small"
                              variant="text"
                              color="inherit"
                              onClick={() => {
                                setEditModeSla(false);
                                resetForm();
                              }}
                              disabled={isSubmitting}
                            >
                              Cancelar
                            </Button>
                          )}
                        </>
                      )}

                      {values.slaTimes.length === 0 && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => setEditModeSla(true)}
                        >
                          Crear
                        </Button>
                      )}
                    </Box>
                    <button type="submit" id="btnSubmitSla" style={{ display: 'none' }}></button>
                  </Box>
                  <CardContent sx={{ pt: 0, flexGrow: 1 }}>
                    {dataList.length === 0 && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Aún no tiene datos de configuración. Si desea utilizar la configuración
                        recomendada, presione <strong>Guardar</strong>.
                      </Alert>
                    )}
                    <FieldArray name="slaTimes">
                      {({ push, remove }) => (
                        <>
                          <TableContainer component={Paper} elevation={0} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <StyledHeaderCell>Nivel de Riesgo</StyledHeaderCell>
                                  <StyledHeaderCell>Primera Respuesta (SLA)</StyledHeaderCell>
                                  <StyledHeaderCell>Resolución (SLA)</StyledHeaderCell>
                                  {editModeSla && <StyledHeaderCell>Acciones</StyledHeaderCell>}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {values.slaTimes.map((row: any, index: number) => {
                                  const isSavedInBackend =
                                    dataList.length > 0 && !String(row.id).startsWith('sla-');
                                  const isAdded = autoAlerts.some(
                                    (alert: any) =>
                                      alert.sourceSlaId === row.id || alert.alertLevel === row.level
                                  );
                                  const canDrag = !editModeSla && !isAdded && isSavedInBackend;

                                  return (
                                    <TableRow
                                      key={row.id || index}
                                      draggable={canDrag}
                                      onDragStart={(e) =>
                                        canDrag ? handleDragStart(e, row) : e.preventDefault()
                                      }
                                      sx={{
                                        cursor: editModeSla
                                          ? 'default'
                                          : canDrag
                                            ? 'grab'
                                            : 'not-allowed',
                                        '&:hover': {
                                          backgroundColor: canDrag ? 'rgba(0,0,0,0.04)' : 'inherit',
                                        },
                                      }}
                                      title={
                                        !editModeSla
                                          ? !isSavedInBackend
                                            ? 'Guarde la configuración antes de arrastrar'
                                            : isAdded
                                              ? 'Ya asignado a Alertas Automáticas'
                                              : 'Arrastra a Alertas Automáticas'
                                          : ''
                                      }
                                    >
                                      <StyledTableCell>
                                        {editModeSla ? (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              gap: 0.5,
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                            }}
                                          >
                                            <SelectField
                                              name={`slaTimes.${index}.icon`}
                                              size="small"
                                              sx={{ width: 70 }}
                                              data={[
                                                { label: '😡', value: '😡' },
                                                { label: '⚠️', value: '⚠️' },
                                                { label: '😐', value: '😐' },
                                                { label: '🙂', value: '🙂' },
                                                { label: '❓', value: '❓' },
                                                { label: '✅', value: '✅' },
                                                { label: '❌', value: '❌' },
                                                { label: '🔥', value: '🔥' },
                                              ]}
                                            />
                                            <InputField
                                              name={`slaTimes.${index}.level`}
                                              size="small"
                                              sx={{ minWidth: 90 }}
                                              placeholder="Nivel"
                                            />
                                            <InputField
                                              type="color"
                                              name={`slaTimes.${index}.color`}
                                              size="small"
                                              sx={{
                                                width: 50,
                                                p: 0,
                                                '& input': { padding: '2px', cursor: 'pointer' },
                                              }}
                                            />
                                          </Box>
                                        ) : (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                              justifyContent: 'center',
                                            }}
                                          >
                                            <span>{row.icon || '❓'}</span>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                              {row.level}
                                            </Typography>
                                            <Box
                                              sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: row.color || '#ccc',
                                              }}
                                            ></Box>
                                            {isAdded && (
                                              <CheckCircleOutlineIcon
                                                color="success"
                                                sx={{ fontSize: 16 }}
                                              />
                                            )}
                                          </Box>
                                        )}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {editModeSla ? (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              gap: 0.5,
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                            }}
                                          >
                                            <InputField
                                              type="number"
                                              name={`slaTimes.${index}.firstResponseValue`}
                                              size="small"
                                              sx={{ width: 80 }}
                                            />
                                            <SelectField
                                              name={`slaTimes.${index}.firstResponseUnit`}
                                              size="small"
                                              sx={{ width: 120 }}
                                              data={[
                                                { label: 'minutos', value: 'minutos' },
                                                { label: 'horas', value: 'horas' },
                                              ]}
                                            />
                                          </Box>
                                        ) : (
                                          `${row.firstResponseValue} ${row.firstResponseUnit}`
                                        )}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {editModeSla ? (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              gap: 0.5,
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                            }}
                                          >
                                            <InputField
                                              type="number"
                                              name={`slaTimes.${index}.resolutionValue`}
                                              size="small"
                                              sx={{ width: 80 }}
                                            />
                                            <SelectField
                                              name={`slaTimes.${index}.resolutionUnit`}
                                              size="small"
                                              sx={{ width: 120 }}
                                              data={[
                                                { label: 'minutos', value: 'minutos' },
                                                { label: 'horas', value: 'horas' },
                                              ]}
                                            />
                                          </Box>
                                        ) : (
                                          `${row.resolutionValue} ${row.resolutionUnit}`
                                        )}
                                      </StyledTableCell>
                                      {editModeSla && (
                                        <StyledTableCell>
                                          <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() => {
                                              if (isSavedInBackend) {
                                                setSlaToDeleteInfo({ index, removeFn: remove });
                                                setOpenSlaDeactivateModal(true);
                                              } else {
                                                remove(index);
                                              }
                                            }}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </StyledTableCell>
                                      )}
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          {editModeSla && (
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                  push({
                                    id: `sla-${Date.now()}`,
                                    level: 'Nuevo',
                                    firstResponseValue: 1,
                                    firstResponseUnit: 'horas',
                                    resolutionValue: 1,
                                    resolutionUnit: 'horas',
                                    icon: '❓',
                                    color: '#ccc',
                                  })
                                }
                              >
                                Agregar Fila
                              </Button>
                            </Box>
                          )}
                        </>
                      )}
                    </FieldArray>
                  </CardContent>
                </Card>
              </Grid>

              {/* B. REGLAS AUTOMATICAS */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 2,
                    boxShadow: 2,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                      B. REGLAS AUTOMÁTICAS
                    </Typography>
                  </Box>
                  <CardContent
                    sx={{ pt: 0, display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1 }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ mb: 1, display: 'block' }}
                      >
                        Asignación de prioridad según NPS (Escala 1 al 5)
                      </Typography>
                      <TableContainer component={Paper} elevation={0} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <StyledHeaderCell>NPS</StyledHeaderCell>
                              <StyledHeaderCell>Prioridad</StyledHeaderCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <StyledTableCell>1</StyledTableCell>
                              <StyledTableCell sx={{ color: '#FF4C51', fontWeight: 'bold' }}>
                                Crítica
                              </StyledTableCell>
                            </TableRow>
                            <TableRow>
                              <StyledTableCell>2</StyledTableCell>
                              <StyledTableCell sx={{ color: '#e53e3e', fontWeight: 'bold' }}>
                                Alta
                              </StyledTableCell>
                            </TableRow>
                            <TableRow>
                              <StyledTableCell>3</StyledTableCell>
                              <StyledTableCell sx={{ color: '#F1C40F', fontWeight: 'bold' }}>
                                Media
                              </StyledTableCell>
                            </TableRow>
                            <TableRow>
                              <StyledTableCell>4-5</StyledTableCell>
                              <StyledTableCell sx={{ color: '#5DCC0A', fontWeight: 'bold' }}>
                                Baja
                              </StyledTableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>

                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5, flexGrow: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}
                      >
                        Palabras críticas 😡 (ejemplos)
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                        {(Array.isArray(critical_words) ? critical_words : []).map((word: any) => (
                          <Chip
                            key={word.id}
                            label={word.keyword}
                            size="small"
                            onDelete={() => handleDeleteKeywordClick(word.id)}
                          />
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Nueva palabra"
                          value={newWord}
                          onChange={(e) => setNewWord(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddWord()}
                          fullWidth
                        />
                        <Button variant="contained" size="small" onClick={handleAddWord}>
                          <AddIcon />
                        </Button>
                      </Box>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ mt: 1, display: 'block' }}
                      >
                        Si el comentario contiene estas palabras, sube prioridad automáticamente a
                        critica.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* C. ALERTAS AUTOMATICAS */}
              <Grid size={{ xs: 12, md: 7 }}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 2,
                    boxShadow: 2,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                      C. ALERTAS AUTOMÁTICAS
                    </Typography>
                  </Box>
                  <CardContent
                    sx={{
                      pt: 0,
                      flexGrow: 1,
                      minHeight: 200,
                      border: '2px dashed transparent',
                      '&:hover': { border: '2px dashed #663cc1', borderRadius: 1 },
                    }}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    {autoAlerts.length === 0 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '100%',
                          flexDirection: 'column',
                          color: 'text.secondary',
                        }}
                      >
                        <Typography variant="body2">Arrastra tiempos de SLA aquí</Typography>
                      </Box>
                    ) : (
                      <TableContainer component={Paper} elevation={0} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <StyledHeaderCell>Tiempo sin gestión</StyledHeaderCell>
                              <StyledHeaderCell>Nivel de Alerta</StyledHeaderCell>
                              <StyledHeaderCell>Acción</StyledHeaderCell>
                              <StyledHeaderCell>Opciones</StyledHeaderCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {autoAlerts.map((row) => (
                              <TableRow key={row.id}>
                                <StyledTableCell>{row.timeWithoutManagement}</StyledTableCell>
                                <StyledTableCell>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: row.color,
                                      }}
                                    ></Box>
                                    <Typography variant="body2">{row.alertLevel}</Typography>
                                  </Box>
                                </StyledTableCell>
                                <StyledTableCell>
                                  {row.isNew || row.isEditing ? (
                                    <Select
                                      size="small"
                                      value={row.action}
                                      onChange={(e) =>
                                        handleAlertActionChange(row.id, e.target.value)
                                      }
                                      fullWidth
                                    >
                                      {actionsList.length > 0
                                        ? actionsList.map((a: any) => (
                                            <MenuItem key={a.value} value={a.value}>
                                              {a.label}
                                            </MenuItem>
                                          ))
                                        : ALERT_ACTIONS.map((a) => (
                                            <MenuItem key={a.value} value={a.value}>
                                              {a.label}
                                            </MenuItem>
                                          ))}
                                    </Select>
                                  ) : (
                                    <Typography variant="body2">
                                      {actionsList.find((a: any) => a.value === row.action)
                                        ?.label || getAlertActionLabel(row.action)}
                                    </Typography>
                                  )}
                                </StyledTableCell>
                                <StyledTableCell>
                                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                    {row.isNew || row.isEditing ? (
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() => handleSaveAlert(row.id)}
                                        title="Guardar"
                                      >
                                        <SaveIcon fontSize="small" />
                                      </IconButton>
                                    ) : (
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() => handleEditAlert(row.id)}
                                        title="Editar"
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() => handleDeleteAlert(row.id)}
                                      title="Eliminar"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </StyledTableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>

      {/* LOWER SECTION */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
                5. LEYENDA DE ICONOS Y ESTADOS
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                    <CheckCircleOutlineIcon color="success" />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Sin alertas
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Caso sin novedades
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                    <WarningAmberIcon sx={{ color: '#F1C40F' }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Atención
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        SLA próximo a vencer
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <ErrorOutlineIcon color="error" />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        SLA vencido
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Seguimiento requerido
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Chip
                      label="Caso Abierto"
                      size="small"
                      sx={{ backgroundColor: '#ffebee', color: '#c62828' }}
                    />
                    <Typography variant="caption">Caso en seguimiento</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Chip
                      label="En proceso"
                      size="small"
                      sx={{ backgroundColor: '#fff3e0', color: '#ef6c00' }}
                    />
                    <Typography variant="caption">Seguimiento en curso</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label="Cerrado"
                      size="small"
                      sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
                    />
                    <Typography variant="caption">Caso finalizado</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
            <Box
              sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Box>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
                  6. BENEFICIOS PARA LA ORGANIZACIÓN
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    'Detección inmediata de clientes inconformes.',
                    'Seguimiento efectivo y oportuno.',
                    'Control de tiempos y cumplimiento de SLA.',
                    'Mejora continua en la experiencia del cliente.',
                  ].map((benefit, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CheckCircleOutlineIcon color="primary" sx={{ fontSize: 22 }} />
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, color: 'text.primary', letterSpacing: '0.2px' }}
                      >
                        {benefit}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box sx={{ display: { xs: 'none', sm: 'block' }, opacity: 0.8 }}>
                <AssignmentIcon sx={{ fontSize: 100, color: '#663cc1' }} />
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
